import { useMemo, type ReactNode } from "react";
import { ReadingLayout } from "../reading-layout";
import {
  useProvideRailExtension,
  type RailExtension,
} from "../../rail-extension-context";
import { FileText } from "../../icons";
import { MarkdownProse } from "../markdown-prose";
import { ArticleHeader } from "./article-header";
import { deriveToc, readingMinutes, tocGroupsFromItems } from "./toc";
import type {
  ArticleBody,
  ArticleMetaModel,
  ArticleRailLink,
  ArticleTocItem,
} from "./types";

export interface ArticleShellProps {
  title: string;
  eyebrow?: ReactNode;
  dek?: ReactNode;
  meta?: ArticleMetaModel;
  /** Header-baseline actions slot. */
  actions?: ReactNode;
  body: ArticleBody;
  /** Below-the-body slot (feedback / subscribe / apply / regen note). */
  footer?: ReactNode;
  /** Related articles / sibling docs for the right rail. The on-this-page TOC
   *  is derived from the body and added automatically. `activeHref` highlights
   *  the current item in the link list (e.g. the doc being read). */
  rail?: { title?: string; activeHref?: string; links?: ArticleRailLink[] };
  /** Reading measure for the centered column (default 44rem). The rail floats
   *  half this past page-centre. */
  measure?: string;
}

const DEFAULT_MEASURE_REM = 44;

/**
 * ONE reading surface for every long-form content type — Help, Docs, Blog,
 * Changelog, Careers. The header + centered `ReadingLayout` column + body
 * (`MarkdownProse`) are identical for every kind; surfaces vary only by two
 * slots (`actions`, `footer`) and the rail links.
 *
 * It plugs into the real shell chrome, never mocking it:
 *  - the on-this-page TOC + related/sibling links are pushed into the existing
 *    right `ContextRail` via `useProvideRailExtension` (scroll-spy, marker,
 *    < lg hide owned there) — the mirror of how the modes float on the left;
 *  - the breadcrumb is the shell top-nav (path-derived) — this renders none;
 *  - the column is centered by `ReadingLayout`, the rail floats in the margin.
 *
 * See docs/design/UNIFIED-ARTICLE-SYSTEM-PRD.md.
 */
export function ArticleShell({
  title,
  eyebrow,
  dek,
  meta,
  actions,
  body,
  footer,
  rail,
  measure,
}: ArticleShellProps) {
  const measureRem = parseRem(measure) ?? DEFAULT_MEASURE_REM;

  const toc: ArticleTocItem[] = useMemo(
    () => (body.kind === "markdown" ? deriveToc(body.md) : (body.toc ?? [])),
    [body],
  );
  const minutes = useMemo(() => {
    if (meta?.readingMinutes) return meta.readingMinutes;
    if (body.kind === "markdown") return readingMinutes(body.md);
    return body.kind === "node" ? body.minutes : undefined;
  }, [body, meta?.readingMinutes]);

  const tocGroups = useMemo(() => tocGroupsFromItems(toc), [toc]);
  const railLinks = rail?.links ?? [];

  // Push the on-this-page TOC + related/sibling links into the REAL right
  // ContextRail — the same seam DocumentView uses. ContextRail owns the
  // scroll-spy marker, the dim/badge styling, and the < lg hide. Push `null`
  // (no rail) when there's nothing to show — a body with no headings and no
  // related links — so a chrome-less surface doesn't get an empty gutter rail.
  const railExtension = useMemo<RailExtension | null>(() => {
    if (railLinks.length === 0 && tocGroups.length === 0) return null;
    return {
      side: "right",
      placement: "centered",
      tocOffsetRem: measureRem / 2,
      groupLabel: rail?.title ?? "On this page",
      activeHref: rail?.activeHref ?? "",
      links: railLinks.map((l) => ({
        href: l.href,
        label: l.title,
        icon: <FileText strokeWidth={1.5} />,
        dim: l.dim,
        badge: l.hint,
      })),
      toc: tocGroups,
    };
  }, [measureRem, rail?.title, rail?.activeHref, railLinks, tocGroups]);
  useProvideRailExtension(railExtension);

  const hasFallback = railLinks.length > 0 || toc.length > 0;

  return (
    <div className="pb-24 pt-2">
      <ReadingLayout measure={measure}>
        <ArticleHeader
          title={title}
          eyebrow={eyebrow}
          dek={dek}
          meta={meta}
          minutes={minutes}
          actions={actions}
        />

        {/* Narrow-width fallback — the right ContextRail is hidden < lg, so
            surface the same related links + on-this-page nav as disclosures. */}
        {hasFallback && (
          <div className="mb-8 space-y-2 lg:hidden">
            {railLinks.length > 0 ? (
              <details className="rounded-md border border-border/60 px-3 py-2">
                <summary className="cursor-pointer type-body text-foreground">
                  {rail?.title ?? "Related"}
                </summary>
                <ul className="mt-3 space-y-1.5">
                  {railLinks.map((l) => (
                    <li key={`${l.href}|${l.title}`}>
                      <a
                        href={l.href}
                        className="type-body text-muted-foreground hover:text-foreground"
                      >
                        {l.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
            {toc.length > 0 ? (
              <details className="rounded-md border border-border/60 px-3 py-2">
                <summary className="cursor-pointer type-body text-foreground">
                  On this page
                </summary>
                <ul className="mt-3 space-y-1.5">
                  {toc.map((t) => (
                    <li
                      key={t.slug}
                      className={t.level === 3 ? "pl-3" : undefined}
                    >
                      <a
                        href={`#${t.slug}`}
                        className="type-body text-muted-foreground hover:text-foreground"
                      >
                        {t.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </div>
        )}

        {/* Body — the already-DRY renderer (markdown) or a pre-rendered node
            (the doc block sequence). */}
        {body.kind === "markdown" ? (
          <MarkdownProse content={body.md} />
        ) : (
          body.content
        )}

        {footer}
      </ReadingLayout>
    </div>
  );
}

function parseRem(measure?: string): number | null {
  if (!measure) return null;
  const m = /^([\d.]+)rem$/.exec(measure.trim());
  return m ? parseFloat(m[1]!) : null;
}
